import { type ReactNode } from "react";
import { Link } from "wouter";
import { Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { PRO_FEATURES, type ProFeatureKey } from "@workspace/pricing";
import { UpgradeCta } from "@/components/upgrade-cta";

const CARD_STYLES = {
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  gap: "0.625rem",
  padding: "1.5rem 2rem",
  borderRadius: "0.75rem",
  backgroundColor: "hsl(var(--background))",
  border: "1px solid hsl(var(--border))",
  boxShadow: "0 4px 24px 0 rgba(0,0,0,0.10)",
  textAlign: "center" as const,
  maxWidth: "340px",
};

function TeaserCardInner({ title, description }: { title: string; description: string }) {
  return (
    <div style={CARD_STYLES}>
      <div style={{ width: "2.5rem", height: "2.5rem", borderRadius: "9999px", backgroundColor: "hsl(var(--primary) / 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Lock style={{ width: "1.25rem", height: "1.25rem", color: "hsl(var(--primary))" }} />
      </div>
      <div>
        <p style={{ fontWeight: 700, fontSize: "1rem", marginBottom: "0.25rem" }}>{title}</p>
        <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", lineHeight: 1.5 }}>{description}</p>
      </div>
      <Link href="/upgrade">
        <Button size="sm" className="gap-1.5 mt-1">
          <Sparkles style={{ width: "0.875rem", height: "0.875rem" }} />
          Upgrade to Pro
        </Button>
      </Link>
    </div>
  );
}

/**
 * Wraps blurred sample content in a gated section. The upgrade card is
 * sticky — it stays visible at the top of the viewport as the blurred
 * content scrolls behind it, so users never have to hunt for it on long pages.
 *
 * Usage:
 *   <ProTeaserSection title="Feature" description="What it does.">
 *     {sampleContent}
 *   </ProTeaserSection>
 */
export function ProTeaserSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div style={{ position: "relative" }}>
      {/* height:0 + overflow:visible means this takes no space in the flow but
          renders the card visually at the top of the container. sticky keeps it
          anchored to the viewport as the blurred content below scrolls. */}
      <div
        style={{
          position: "sticky",
          top: "2rem",
          zIndex: 10,
          height: 0,
          overflow: "visible",
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <div style={{ pointerEvents: "all" }}>
          <TeaserCardInner title={title} description={description} />
        </div>
      </div>
      {/* Blurred sample content — scrolls normally behind the sticky card */}
      <div style={{ filter: "blur(2px)", opacity: 0.45, pointerEvents: "none", userSelect: "none" }}>
        {children}
      </div>
    </div>
  );
}

/**
 * Standalone upgrade card for use inside a custom teaser layout (e.g. when
 * you already have your own blur + absolute positioning, like FieldProTeaser).
 */
export function ProTeaserCard({ title, description }: { title: string; description: string }) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "2rem",
        zIndex: 10,
      }}
    >
      <TeaserCardInner title={title} description={description} />
    </div>
  );
}

interface ProGateProps {
  feature: ProFeatureKey;
  children: ReactNode;
  mode?: "overlay" | "overlay-button" | "inline" | "lock-icon" | "button-lock";
  /** Opens the gate for an account that has this feature some other way than Pro. */
  unlocked?: boolean;
}

export function ProGate({ feature, children, mode = "overlay", unlocked = false }: ProGateProps) {
  const { user } = useAuth();

  const { hasProAccess } = getTrialInfo(user);

  if (hasProAccess || unlocked) {
    return <>{children}</>;
  }

  const featureLabel = PRO_FEATURES[feature];

  if (mode === "inline") {
    return <UpgradeCta message={`${featureLabel} is available on the Pro plan.`} />;
  }

  if (mode === "lock-icon") {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <Link href="/upgrade">
            <Button variant="outline" aria-label="Pro feature — upgrade to unlock" size="icon">
              <Lock className="w-4 h-4" />
            </Button>
          </Link>
        </TooltipTrigger>
        <TooltipContent>Upgrade to Pro to unlock {featureLabel}</TooltipContent>
      </Tooltip>
    );
  }

  if (mode === "overlay-button") {
    return (
      <div className="relative group/btngate inline-flex overflow-hidden rounded-md">
        <div className="pointer-events-none select-none opacity-40 group-hover/btngate:blur-[1.5px] transition-[filter] duration-200">
          {children}
        </div>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Link
              href="/upgrade"
              aria-label={`Upgrade to Pro to unlock ${featureLabel}`}
              className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-background/70 opacity-0 group-hover/btngate:opacity-100 transition-opacity duration-200 hover:bg-background/85"
            >
              <Lock className="w-4 h-4 text-foreground" />
            </Link>
          </TooltipTrigger>
          <TooltipContent>Upgrade to Pro to unlock {featureLabel}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  if (mode === "button-lock") {
    return (
      <div className="relative group/btnlock inline-flex rounded-md ring-1 ring-amber-400 dark:ring-amber-600">
        <div className="pointer-events-none select-none opacity-75">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center z-10 rounded-md opacity-0 group-hover/btnlock:opacity-100 transition-opacity duration-150 bg-amber-50/90 dark:bg-amber-950/70">
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Link href="/upgrade">
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Pro feature — upgrade to unlock"
                  className="border-amber-400 bg-transparent text-amber-700 hover:bg-amber-100 hover:border-amber-500 dark:border-amber-600 dark:text-amber-400 dark:hover:bg-amber-900/50 gap-1.5"
                >
                  <Lock className="w-3.5 h-3.5 shrink-0" />
                  <span>Upgrade</span>
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent>Upgrade to Pro to unlock {featureLabel}</TooltipContent>
          </Tooltip>
        </div>
      </div>
    );
  }

  return (
    <div className="relative group">
      <div className="pointer-events-none select-none opacity-40 group-hover:blur-[1.5px] transition-[filter] duration-200">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/50 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <Link href="/upgrade">
          <Button size="sm" className="gap-1.5 shadow-md">
            <Lock className="w-3.5 h-3.5" />
            Upgrade to Pro
          </Button>
        </Link>
      </div>
    </div>
  );
}
